import React from "react";
import { type Integration } from "lib/sanity";
import { Section } from "components/section";
import { TextTitle } from "ui/text";
import { MediaAccordion } from "ui/accordion";
import Image from "next/image";
import { Flex, type FlexProps, type SpaceProps } from "@chakra-ui/react";
import { integrationsCount, sourcesCount } from "../../constants/integrations";
import accordionImage1 from "../../../public/assets/activate-data-steps/accordion-1.png";
import accordionImage2 from "../../../public/assets/activate-data-steps/accordion-2.png";
import { Step3Figure } from "./step-3/step-3-figure";

type ActivateDataProps = {
  source: Integration;
  destination: Integration;
} & SpaceProps;

export function ActivateDataSteps({ source, destination, ...props }: ActivateDataProps) {
  const mediaRatio = 988 / 779;
  return (
    <Section my="0" mb={{ base: 0, lg: "sectionSpacingY.md" }} {...props}>
      <TextTitle
        size="lg"
        align="center"
        title="Activate your data in 3 easy steps"
        mb={{ base: "bigger", md: "biggest" }}
      />

      <MediaAccordion
        variant="dynamic"
        direction="right"
        mediaRatio={mediaRatio}
        items={[
          {
            title: "Step 1: Connect your source and destination",
            content: `Connect to ${sourcesCount}+ data sources, like ${
              source?.name ?? "Snowflake"
            }, and ${integrationsCount}+ destinations, like ${destination?.name ?? "Salesforce"}.`,
            media: (
              <ActivationStepMedia bg="new.yellow.150">
                <Image
                  src={accordionImage1}
                  alt="An illustration showing logos of various sources connecting to destination logos."
                />
              </ActivationStepMedia>
            ),
          },
          {
            title: "Step 2: Define your model",
            content: "Use SQL or select an existing dbt or Looker model.",
            media: (
              <ActivationStepMedia bg="new.violet.150">
                <Image src={accordionImage2} alt="An illustration showing a dbt users model with a SQL query." />
              </ActivationStepMedia>
            ),
          },
          {
            title: "Step 3: Sync your data",
            content: `Map your fields, set your sync schedule, and hit “Go!”`,
            media: (
              <ActivationStepMedia bg="new.green.150">
                <Step3Figure source={source} destination={destination} />
              </ActivationStepMedia>
            ),
            mediaRatio: { base: 1.15, sm: mediaRatio },
          },
        ]}
      />
    </Section>
  );
}

function ActivationStepMedia({ bg, children }: FlexProps) {
  return (
    <Flex boxSize="100%" bg={bg} p={{ base: "small", lg: "biggest" }} align="center" justify="center">
      <Flex
        w="100%"
        h="100%"
        maxH="100%"
        bg="brand.background.light"
        borderRadius="small"
        overflow="hidden"
        boxShadow="md"
        alignItems="center"
      >
        {children}
      </Flex>
    </Flex>
  );
}
